// src/Pages/Finance/DriverExpense.jsx

"use client";

import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import {
  TbSearch,
  TbX,
  TbFilter,
  TbCalendar,
  TbChevronDown,
  TbCheck,
  TbInfoCircle,
  TbCircleCheck,
  TbAlertCircle,
  TbTrash,
  TbUser,
  TbPencil,
  TbPaperclip,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";

// Componente Snackbar
function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}>
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function DriverExpense() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ paginação vinda do backend
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // filtros
  const [searchQuery, setSearchQuery] = useState(""); // client-side (na página atual)
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [paidByFilter, setPaidByFilter] = useState("all");

  // ✅ date range
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // driver filter custom
  const [drivers, setDrivers] = useState([]);
  const [driveSearch, setDriveSearch] = useState("");
  const [selectedDrive, setSelectedDrive] = useState("all");
  const [showDrivesList, setShowDrivesList] = useState(false);
  const driveSearchRef = useRef(null);

  // expand attachments (linha da tabela)
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);

  // delete state (despesa)
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);

  // modal edit
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditExpense, setCurrentEditExpense] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingExpenseDetails, setLoadingExpenseDetails] = useState(false);
  const modalRef = useRef(null);

  // ✅ anexos no modal (add/remove)
  const [newFiles, setNewFiles] = useState([]);
  const [newFilesDesc, setNewFilesDesc] = useState("");
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);

  // snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // ✅ KPIs vindos do backend (consideram TODOS os itens elegíveis do filtro)
  const [kpis, setKpis] = useState({
    total: 0,
    thisMonth: 0,
    totalAttachments: 0,
    avg: 0,
  });

  const isResettingFiltersRef = useRef(false);

  const categoryOptions = [
    { value: "Fuel", label: "Fuel" },
    { value: "Storage Fee", label: "Storage Fee" },
    { value: "Maintenance", label: "Maintenance" },
    { value: "Supplies", label: "Supplies" },
    { value: "Personal", label: "Personal" },
    { value: "Owner Advance", label: "Owner Advance" },
    { value: "Other", label: "Other" },
  ];

  const paidByOptions = [
    { value: "Driver", label: "Driver" },
    { value: "Company", label: "Company" },
  ];

  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  const STORAGE_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_STORAGE_ORIGIN || "https://new.cajuu.app";
  }, []);

  const showSnackbar = (message, type = "success") => {
    setSnackbar({
      open: true,
      message,
      type,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getToken = () => localStorage.getItem("token");

  const dateOnly = (dt) => {
    if (!dt) return "";
    const s = String(dt);
    if (s.includes("T")) return s.split("T")[0];
    if (s.includes(" ")) return s.split(" ")[0];
    return s;
  };

  const normalizeDateTimeLocalToSql = (dtLocal) => {
    if (!dtLocal) return "";
    const s = String(dtLocal);
    if (s.includes("T") && s.length === 16) {
      return s.replace("T", " ") + ":00";
    }
    return s.replace("T", " ");
  };

  // ✅ evitar bug de timezone quando recebe YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const s = String(dateString);
    const safe = s.length === 10 ? `${s}T12:00:00` : s;
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(safe).toLocaleDateString("en-US", options);
  };

  const formatCurrency = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const storageUrl = (path) => {
    if (!path) return null;
    const p = String(path).replace(/^\/+/, "");
    return `${STORAGE_ORIGIN.replace(/\/+$/, "")}/storage/${p}`;
  };

  const driverLabelFromDriverApi = (d) => {
    const u = d?.user;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (u?.email) return u.email;
    return `Driver #${d?.id ?? ""}`;
  };

  const findDriverName = (driverId, fallbackName) => {
    if (fallbackName) return fallbackName;
    const d = drivers.find((x) => String(x.id) === String(driverId));
    return d ? d.name : "N/A";
  };

  const mapExpenseFromApi = (e) => {
    const attachments =
      e?.attachments ||
      e?.driver_expense_attachments ||
      e?.driverExpenseAttachments ||
      e?.driver_expense_attachments?.data ||
      [];

    const safeAttachments = Array.isArray(attachments) ? attachments : [];

    const expenseAt = e.expense_at || e.expenseAt || e.created_at || e.createdAt;
    const driverId = e.driver_id ?? e.driverId ?? e.driver?.id ?? null;

    const driverNameFromPayload =
      e?.driver?.user
        ? `${e.driver.user.first_name || ""} ${e.driver.user.last_name || ""}`.trim()
        : e?.driver?.name || null;

    const amount = Number(e.amount ?? 0);

    const orderId = e?.order_id ?? e?.orderId ?? null;
    const buyerNumber = e?.buyer_number ?? e?.buyerNumber ?? null;
    const lotNumber = e?.lot_number ?? e?.lotNumber ?? null;

    return {
      id: (e.id ?? "").toString(),
      original_id: e.id,
      driver_id: driverId,
      driver_name: driverNameFromPayload || null,
      category: e.category || "N/A",
      paid_by: e.paid_by ?? e.paidBy ?? null,
      description: e.description || "",
      amount: amount,
      expense_at: expenseAt || "",
      date: dateOnly(expenseAt) || dateOnly(e.created_at),
      attachments: safeAttachments,
      created_at: e.created_at || "",
      order_id: orderId !== null && orderId !== undefined ? String(orderId) : "",
      buyer_number: buyerNumber !== null && buyerNumber !== undefined ? String(buyerNumber) : "",
      lot_number: lotNumber !== null && lotNumber !== undefined ? String(lotNumber) : "",
      original: e,
    };
  };

  const fetchDrivers = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_ORIGIN}/api/driver`, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const list =
        (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
        (Array.isArray(res?.data?.data) && res.data.data) ||
        (Array.isArray(res?.data) && res.data) ||
        [];

      const mapped = list.map((d) => ({
        id: d.id,
        name: driverLabelFromDriverApi(d),
      }));

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setDrivers(mapped);
    } catch (error) {
      setDrivers([]);
    }
  };

  const buildServerParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage || 10,
      sort_by: "expense_at",
      sort_direction: "desc",
      with_kpis: 1, // ✅ garante KPIs
    };

    if (selectedDrive !== "all") params.driver_id = selectedDrive;
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (paidByFilter !== "all") params.paid_by = paidByFilter;

    // datas: manda datetime pra incluir o dia inteiro
    if (dateFrom) params.date_from = `${dateFrom} 00:00:00`;
    if (dateTo) params.date_to = `${dateTo} 23:59:59`;

    return params;
  };

  const parsePaginator = (apiPayload) => {
    // formato esperado: { data: { current_page, data: [...], last_page, ... , kpis? }, message: "" }
    if (apiPayload?.data && typeof apiPayload.data === "object" && Array.isArray(apiPayload.data.data)) {
      return apiPayload.data;
    }

    // fallback: paginator direto { current_page, data: [...] }
    if (apiPayload?.current_page && Array.isArray(apiPayload.data)) {
      return apiPayload;
    }

    return null;
  };

  const fetchExpenses = async (page = 1) => {
    setLoading(true);
    try {
      const token = getToken();

      const response = await axios.get(`${API_ORIGIN}/api/driverexpense`, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
        params: buildServerParams(page),
      });

      const paginator = parsePaginator(response.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      const mapped = list.map(mapExpenseFromApi);

      // paginação backend
      const cp = Number(paginator?.current_page ?? page) || 1;
      const lp = Number(paginator?.last_page ?? 1) || 1;
      const tt = Number(paginator?.total ?? mapped.length) || 0;
      const pp = Number(paginator?.per_page ?? perPage) || perPage;

      setExpenses(mapped);
      setCurrentPage(cp);
      setTotalPages(lp);
      setTotalItems(tt);
      setPerPage(pp);

      setRangeFrom(Number(paginator?.from ?? (mapped.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to ?? mapped.length) || 0);

      // ✅ KPIs do backend (fallback: calcula da página caso não venha)
      const serverKpis = paginator?.kpis;
      if (serverKpis && typeof serverKpis === "object") {
        setKpis({
          total: Number(serverKpis.total ?? 0),
          thisMonth: Number(serverKpis.thisMonth ?? 0),
          totalAttachments: Number(serverKpis.totalAttachments ?? 0),
          avg: Number(serverKpis.avg ?? 0),
        });
      } else {
        const total = mapped.reduce((sum, e) => sum + Number(e.amount || 0), 0);
        const now = new Date();
        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const thisMonth = mapped
          .filter((e) => (e.date || "").startsWith(ym))
          .reduce((sum, e) => sum + Number(e.amount || 0), 0);
        const totalAttachments = mapped.reduce(
          (sum, e) => sum + (Array.isArray(e.attachments) ? e.attachments.length : 0),
          0
        );
        const avg = mapped.length > 0 ? total / mapped.length : 0;
        setKpis({ total, thisMonth, totalAttachments, avg });
      }

      // se trocar de página/filtro, fecha attachments expandidos
      setExpandedExpenseId(null);
    } catch (error) {
      console.error("Error fetching driver expenses:", error);
      setExpenses([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);
      setKpis({ total: 0, thisMonth: 0, totalAttachments: 0, avg: 0 });
      showSnackbar("Erro ao carregar driver expenses", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseDetails = async (expenseId) => {
    if (!expenseId) return null;

    setLoadingExpenseDetails(true);
    try {
      const token = getToken();

      const res = await axios.get(`${API_ORIGIN}/api/driverexpense/${expenseId}`, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = res?.data?.data || res?.data;
      if (!payload) return null;

      const mapped = mapExpenseFromApi(payload);

      const expAt = mapped.expense_at ? String(mapped.expense_at) : "";
      let dtLocal = "";
      if (expAt) {
        const s = expAt.includes("T") ? expAt : expAt.replace(" ", "T");
        dtLocal = s.length >= 16 ? s.substring(0, 16) : s;
      }

      const finalMapped = {
        ...mapped,
        expense_at_local: dtLocal,
      };

      setCurrentEditExpense(finalMapped);
      return finalMapped;
    } catch (err) {
      showSnackbar("Erro ao carregar detalhes da despesa", "error");
      return null;
    } finally {
      setLoadingExpenseDetails(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!expense) return;

    const expenseId = expense.original_id || expense.id;

    const ok = window.confirm(
      `Are you sure you want to delete Driver Expense #${expense.id}?\n\nThis action cannot be undone.`
    );

    if (!ok) return;

    setDeletingExpenseId(String(expense.id));

    try {
      const token = getToken();
      const payload = {};

      const response = await axios.delete(`${API_ORIGIN}/api/driverexpense/${expenseId}`, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      });

      if (response.status === 200 || response.status === 204) {
        if (expandedExpenseId === String(expense.id)) {
          setExpandedExpenseId(null);
        }
        await fetchExpenses(currentPage);
        showSnackbar("Driver expense apagado com sucesso!", "success");
      } else {
        throw new Error("Falha ao apagar driver expense");
      }
    } catch (error) {
      console.error("Erro ao apagar driver expense:", error);

      let errorMessage = "Erro ao apagar driver expense";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || "Erro desconhecido da API";
      } else if (error.request) {
        errorMessage = "Erro de rede. Verifique sua conexão.";
      } else {
        errorMessage = "Erro na configuração da requisição.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setDeletingExpenseId(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (driveSearchRef.current && !driveSearchRef.current.contains(event.target)) {
        setShowDrivesList(false);
        if (driveSearch.trim() === "" && selectedDrive !== "all") {
          setTimeout(() => {
            setSelectedDrive("all");
          }, 10);
        }
      }

      if (modalRef.current && !modalRef.current.contains(event.target) && editModalOpen) {
        setEditModalOpen(false);
        setCurrentEditExpense(null);
        setNewFiles([]);
        setNewFilesDesc("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [driveSearch, selectedDrive, editModalOpen]);

  useEffect(() => {
    if (driveSearch.trim() === "" && selectedDrive !== "all") {
      setSelectedDrive("all");
    }
  }, [driveSearch, selectedDrive]);

  const filteredDrives = useMemo(() => {
    return drivers.filter((d) => d.name.toLowerCase().includes(driveSearch.toLowerCase()));
  }, [driveSearch, drivers]);

  useEffect(() => {
    (async () => {
      await fetchDrivers();
      await fetchExpenses(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Sempre que filtros "de backend" mudarem, refaz request na página 1
  useEffect(() => {
    if (isResettingFiltersRef.current) return;

    let from = dateFrom || "";
    let to = dateTo || "";
    if (from && to && from > to) {
      const tmp = from;
      from = to;
      to = tmp;
      setDateFrom(from);
      setDateTo(to);
      return;
    }

    fetchExpenses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, paidByFilter, dateFrom, dateTo, selectedDrive]);

  // ✅ searchQuery permanece client-side (na página atual)
  const visibleExpenses = useMemo(() => {
    let filtered = [...expenses];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((e) => {
        return (
          String(e.id).toLowerCase().includes(q) ||
          String(e.description || "").toLowerCase().includes(q) ||
          String(e.category || "").toLowerCase().includes(q) ||
          String(e.paid_by || "").toLowerCase().includes(q) ||
          String(e.amount || "").toLowerCase().includes(q) ||
          String(e.order_id || "").toLowerCase().includes(q) ||
          String(e.buyer_number || "").toLowerCase().includes(q) ||
          String(e.lot_number || "").toLowerCase().includes(q)
        );
      });
    }

    return filtered;
  }, [searchQuery, expenses]);

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery("");

  const clearFilters = () => {
    isResettingFiltersRef.current = true;

    setSearchQuery("");
    setCategoryFilter("all");
    setPaidByFilter("all");
    setDateFrom("");
    setDateTo("");
    setDriveSearch("");
    setSelectedDrive("all");
    setShowDrivesList(false);
    setExpandedExpenseId(null);

    showSnackbar("Filters cleared", "info");

    // ✅ força reload com page 1 e KPIs do backend
    fetchExpenses(1).finally(() => {
      isResettingFiltersRef.current = false;
    });
  };

  const handleDriveSelect = (driveId, driveName) => {
    setSelectedDrive(driveId);
    setDriveSearch(driveName);
    setShowDrivesList(false);
  };

  const clearDriveFilter = () => {
    setSelectedDrive("all");
    setDriveSearch("");
    setShowDrivesList(false);
  };

  const handleDriveBlur = () => {
    setTimeout(() => {
      setShowDrivesList(false);
    }, 200);
  };

  const toggleExpand = (expenseId) => {
    setExpandedExpenseId((prev) => (prev === expenseId ? null : expenseId));
  };

  // Paginação (backend)
  const goToPage = (pageNumber) => {
    const target = Math.max(1, Math.min(Number(pageNumber) || 1, totalPages || 1));
    if (target === currentPage) return;
    fetchExpenses(target);
  };

  const nextPage = () => {
    if (currentPage >= totalPages) return;
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage <= 1) return;
    goToPage(currentPage - 1);
  };

  // ✅ Modal edit helpers
  const openEdit = async (expenseId) => {
    const expense = expenses.find((x) => x.id === expenseId);
    if (!expense) return;

    setNewFiles([]);
    setNewFilesDesc("");
    setEditModalOpen(true);

    // otimista: abre com dados atuais
    const expAt = expense.expense_at ? String(expense.expense_at) : "";
    let dtLocal = "";
    if (expAt) {
      const s = expAt.includes("T") ? expAt : expAt.replace(" ", "T");
      dtLocal = s.length >= 16 ? s.substring(0, 16) : s;
    }

    setCurrentEditExpense({
      ...expense,
      expense_at_local: dtLocal,
    });

    // depois busca detalhes (anexos atualizados)
    await fetchExpenseDetails(expense.original_id || expense.id);
  };

  const handleSaveEdit = async () => {
    if (!currentEditExpense) return;

    setIsSaving(true);
    try {
      const token = getToken();
      const expenseId = currentEditExpense.original_id || currentEditExpense.id;

      const payload = {
        driver_id: currentEditExpense.driver_id ? parseInt(currentEditExpense.driver_id, 10) : null,
        category: currentEditExpense.category || "Other",
        paid_by: currentEditExpense.paid_by || null,
        amount: parseFloat(currentEditExpense.amount) || 0,
        description: currentEditExpense.description || "",
        expense_at: normalizeDateTimeLocalToSql(currentEditExpense.expense_at_local) || null,
      };

      const response = await axios.post(`${API_ORIGIN}/api/driverexpense/${expenseId}`, payload, {
        headers: {
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        await fetchExpenses(currentPage);
        setEditModalOpen(false);
        setCurrentEditExpense(null);
        setNewFiles([]);
        setNewFilesDesc("");
        showSnackbar("Driver expense atualizado com sucesso!", "success");
      } else {
        throw new Error("Falha ao atualizar driver expense");
      }
    } catch (error) {
      console.error("Erro ao atualizar driver expense:", error);

      let errorMessage = "Erro ao atualizar driver expense";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || "Erro desconhecido da API";
      } else if (error.request) {
        errorMessage = "Erro de rede. Verifique sua conexão.";
      } else {
        errorMessage = "Erro na configuração da requisição.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Upload de anexos (modal)
  const handlePickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setNewFiles(files);
  };

  const handleUploadAttachments = async () => {
    if (!currentEditExpense) return;
    if (!newFiles || newFiles.length === 0) {
      showSnackbar("Selecione ao menos 1 arquivo para enviar", "warning");
      return;
    }

    setIsUploadingAttachments(true);
    try {
      const token = getToken();
      const expenseId = currentEditExpense.original_id || currentEditExpense.id;

      const fd = new FormData();
      newFiles.forEach((file) => fd.append("documents[]", file));
      if (newFilesDesc && String(newFilesDesc).trim()) {
        fd.append("attachment_description", String(newFilesDesc).trim());
      }

      const res = await axios.post(`${API_ORIGIN}/api/driverexpense/${expenseId}/attachments`, fd, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 200 || res.status === 201) {
        showSnackbar("Anexos enviados com sucesso!", "success");
        setNewFiles([]);
        setNewFilesDesc("");

        // atualiza modal + lista + KPIs
        await fetchExpenseDetails(expenseId);
        await fetchExpenses(currentPage);
      } else {
        throw new Error("Falha ao enviar anexos");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao enviar anexos (verifique formato/limite).";
      showSnackbar(msg, "error");
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  // ✅ Remover anexo (modal)
  const handleRemoveAttachment = async (attachment) => {
    if (!currentEditExpense) return;
    if (!attachment?.id) {
      showSnackbar("Attachment inválido", "error");
      return;
    }

    const ok = window.confirm("Deseja remover este anexo? Esta ação não pode ser desfeita.");
    if (!ok) return;

    setRemovingAttachmentId(String(attachment.id));
    try {
      const token = getToken();
      const expenseId = currentEditExpense.original_id || currentEditExpense.id;

      const res = await axios.delete(`${API_ORIGIN}/api/driverexpense/${expenseId}/attachments/${attachment.id}`, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200 || res.status === 204) {
        showSnackbar("Anexo removido com sucesso!", "success");

        await fetchExpenseDetails(expenseId);
        await fetchExpenses(currentPage);
      } else {
        throw new Error("Falha ao remover anexo");
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || "Erro ao remover anexo.";
      showSnackbar(msg, "error");
    } finally {
      setRemovingAttachmentId(null);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="EXPENSES" description="Driver expenses and attachments" />
        </GroupedContainer>

        {/* ✅ Summary cards (KPIs do backend) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Total Expenses (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-red-400">{formatCurrency(kpis.total)}</div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">This Month (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-red-400">{formatCurrency(kpis.thisMonth)}</div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Attachments (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">{Number(kpis.totalAttachments || 0)}</div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Avg / Expense (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">{formatCurrency(kpis.avg)}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
            <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
              <TbX className="w-4 h-4" /> Clear Filters
            </button>
          </div>

          {/* mantém 4 colunas no lg */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Busca */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search (ID, category, paid by, description, amount)"
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            {/* Driver filter custom */}
            <div className="relative" ref={driveSearchRef}>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbSearch className="text-white text-lg" />
                </div>
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={driveSearch}
                  onChange={(e) => {
                    setDriveSearch(e.target.value);
                    if (e.target.value.trim() !== "") {
                      setShowDrivesList(true);
                    }
                  }}
                  onFocus={() => setShowDrivesList(true)}
                  onBlur={handleDriveBlur}
                  className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
                {(driveSearch || selectedDrive !== "all") && (
                  <button
                    onClick={clearDriveFilter}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  >
                    <TbX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showDrivesList && (
                <div className="absolute z-10 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
                  <div className="max-h-48 overflow-y-auto">
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDriveSelect("all", "")}
                      className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="px-2 py-1 text-center text-white text-sm">All Drivers</span>
                      </div>
                      {selectedDrive === "all" && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
                    </div>

                    {filteredDrives.length > 0 ? (
                      <>
                        {filteredDrives.map((d) => (
                          <div
                            key={d.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDriveSelect(String(d.id), d.name)}
                            className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="px-2 py-1 text-center text-white text-sm" title={d.name}>
                                {d.name}
                              </span>
                            </div>
                            {selectedDrive === String(d.id) && (
                              <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        ))}
                        <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                          {driveSearch
                            ? `Found ${filteredDrives.length} of ${drivers.length} drivers`
                            : `Showing all ${filteredDrives.length} drivers`}
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-400 text-sm">No drivers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Category + Paid By */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Category */}
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbFilter className="text-amber-500 text-lg" />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                           appearance-none"
                >
                  <option value="all" className="bg-[#1c1b19]">
                    All Categories
                  </option>
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <TbChevronDown className="text-white text-lg" />
                </div>
              </div>

              {/* Paid By */}
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbUser className="text-gray-400 text-lg" />
                </div>
                <select
                  value={paidByFilter}
                  onChange={(e) => setPaidByFilter(e.target.value)}
                  className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                           appearance-none"
                  title="Paid By (server filter)"
                >
                  <option value="all" className="bg-[#1c1b19]">
                    Paid By (All)
                  </option>
                  {paidByOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <TbChevronDown className="text-white text-lg" />
                </div>
              </div>
            </div>

            {/* Date range */}
            <div className="md:col-span-2 lg:col-span-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* From */}
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">From</span>
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbCalendar className="text-blue-500 text-lg" />
                  </div>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* To */}
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">To</span>
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbCalendar className="text-blue-500 text-lg" />
                  </div>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* info de paginação */}
          <div className="mt-3 text-xs text-white/70">
            Server pagination: page {currentPage} of {totalPages} • total {totalItems} • per page {perPage}
          </div>
        </div>

        {/* Tabela */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Expense ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Buyer Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Lot Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Paid By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Driver
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Attachments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading driver expenses...</p>
                  </td>
                </tr>
              ) : visibleExpenses.length > 0 ? (
                visibleExpenses.map((expense) => {
                  const attCount = Array.isArray(expense.attachments) ? expense.attachments.length : 0;
                  const driverName = findDriverName(expense.driver_id, expense.driver_name);
                  const isDeleting = String(deletingExpenseId) === String(expense.id);

                  return (
                    <Fragment key={expense.id}>
                      <tr className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">#{expense.id}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(expense.date)}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{expense.category || "N/A"}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {expense.order_id ? expense.order_id : "—"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {expense.buyer_number ? expense.buyer_number : "—"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {expense.lot_number ? expense.lot_number : "—"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {expense.paid_by ? expense.paid_by : "—"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <span title={expense.description || ""}>{expense.description ? expense.description : "—"}</span>
                        </td>

                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                            expense.category === "Owner Advance" ? "text-green-400" : "text-red-400"
                          }`}
                        >
                          {formatCurrency(expense.amount)}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{driverName}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <button
                            onClick={() => toggleExpand(expense.id)}
                            className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors inline-flex items-center gap-1"
                            title={expandedExpenseId === expense.id ? "Hide attachments" : "Show attachments"}
                          >
                            <TbChevronDown
                              className={`w-5 h-5 transition-transform ${expandedExpenseId === expense.id ? "rotate-180" : ""}`}
                            />
                            <span className="text-xs">{attCount}</span>
                          </button>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {/* Edit */}
                            <button
                              onClick={() => openEdit(expense.id)}
                              className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                              title="Edit expense (and attachments)"
                            >
                              <TbPencil className="w-5 h-5" />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteExpense(expense)}
                              disabled={isDeleting}
                              className={`p-2 rounded-lg transition-colors ${
                                isDeleting
                                  ? "text-gray-600 cursor-not-allowed"
                                  : "text-gray-400 hover:text-red-400 hover:bg-zinc-700/50"
                              }`}
                              title={isDeleting ? "Deleting..." : "Delete expense"}
                            >
                              <TbTrash className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedExpenseId === expense.id && (
                        <tr className="bg-[#1c1b19]">
                          <td colSpan="12" className="px-6 py-4">
                            <div className="rounded-lg border border-zinc-600 bg-zinc-900/30 p-4">
                              <div className="flex items-center justify-between gap-3 mb-3">
                                <div className="text-sm font-semibold text-white">Attachments ({attCount})</div>
                                <button
                                  onClick={() => toggleExpand(expense.id)}
                                  className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                                >
                                  <TbX className="w-4 h-4" /> Close
                                </button>
                              </div>

                              {attCount === 0 ? (
                                <div className="text-sm text-gray-400">No attachments.</div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {expense.attachments.map((a) => {
                                    const name =
                                      a.original_name || a.name || a.filename || a.path || `Attachment #${a.id}`;
                                    const url = a.path ? storageUrl(a.path) : null;

                                    return (
                                      <div
                                        key={a.id || `${expense.id}-${name}`}
                                        className="border border-zinc-700 rounded-lg p-3 bg-[#1c1b19]/50"
                                      >
                                        <div className="text-sm font-semibold text-white truncate" title={name}>
                                          {name}
                                        </div>

                                        <div className="mt-1 text-xs text-gray-400">
                                          {a.mime_type ? a.mime_type : ""}
                                          {a.size ? ` • ${(Number(a.size) / 1024).toFixed(0)} KB` : ""}
                                        </div>

                                        {a.description ? (
                                          <div className="mt-2 text-xs text-gray-300">{a.description}</div>
                                        ) : null}

                                        {url ? (
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-2 inline-block text-xs text-amber-400 hover:text-amber-300 underline"
                                          >
                                            Open file
                                          </a>
                                        ) : (
                                          <div className="mt-2 text-xs text-gray-500">No file path.</div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="px-6 py-8 text-center text-gray-400">
                    No driver expenses found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação (backend) */}
        {totalPages > 1 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1 || loading}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => goToPage(i)}
                        disabled={loading}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages || loading}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {rangeFrom}-{rangeTo} of {totalItems} expenses
              {searchQuery.trim() ? " (search filter applied on this page)" : ""}
            </div>
          </>
        )}
      </div>

      {/* ✅ Modal de Edição + Gestão de Anexos */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div ref={modalRef} className="bg-[#1c1b19] rounded-xl border border-zinc-600 max-w-4xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">Edit Driver Expense</h2>
                {loadingExpenseDetails && (
                  <span className="text-xs text-white/70 inline-flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500"></span>
                    Loading...
                  </span>
                )}
              </div>

              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setCurrentEditExpense(null);
                  setNewFiles([]);
                  setNewFilesDesc("");
                }}
                className="text-gray-400 hover:text-white"
              >
                <TbX className="w-6 h-6" />
              </button>
            </div>

            {!currentEditExpense ? (
              <div className="text-sm text-gray-400">No expense selected.</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Driver */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Driver</label>
                    <select
                      value={currentEditExpense.driver_id ?? ""}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          driver_id: e.target.value,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="" className="bg-[#1c1b19]">
                        —
                      </option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id} className="bg-[#1c1b19]">
                          {d.name}
                        </option>
                      ))}
                    </select>
                    <div className="text-[11px] text-white/50 mt-1">
                      (Se for usuário Driver, o backend ignora alterações de driver_id)
                    </div>
                  </div>

                  {/* Date / time */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Expense At</label>
                    <input
                      type="datetime-local"
                      value={currentEditExpense.expense_at_local || ""}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          expense_at_local: e.target.value,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      style={{ colorScheme: "dark" }}
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Category</label>
                    <select
                      value={currentEditExpense.category || "Other"}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {currentEditExpense.category === "Other" && (
                      <div className="text-[11px] text-amber-300 mt-1">Category “Other” exige Description.</div>
                    )}
                  </div>

                  {/* Paid By */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Paid By</label>
                    <select
                      value={currentEditExpense.paid_by || ""}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          paid_by: e.target.value || null,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="" className="bg-[#1c1b19]">
                        —
                      </option>
                      {paidByOptions.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={currentEditExpense.amount ?? 0}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Description</label>
                    <input
                      type="text"
                      value={currentEditExpense.description || ""}
                      onChange={(e) =>
                        setCurrentEditExpense((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Description..."
                    />
                  </div>
                </div>

                {/* ✅ Attachments management */}
                <div className="mt-6 rounded-xl border border-zinc-600 p-4 bg-zinc-900/20">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <TbPaperclip className="text-amber-400" />
                      <h3 className="text-sm font-semibold text-white">
                        Attachments (
                        {Array.isArray(currentEditExpense.attachments) ? currentEditExpense.attachments.length : 0})
                      </h3>
                    </div>
                  </div>

                  {/* Existing attachments */}
                  <div className="mt-3">
                    {Array.isArray(currentEditExpense.attachments) && currentEditExpense.attachments.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {currentEditExpense.attachments.map((a) => {
                          const name = a.original_name || a.name || a.filename || a.path || `Attachment #${a.id}`;
                          const url = a.path ? storageUrl(a.path) : null;
                          const isRemoving = String(removingAttachmentId) === String(a.id);

                          return (
                            <div
                              key={a.id || `${currentEditExpense.id}-${name}`}
                              className="border border-zinc-700 rounded-lg p-3 bg-[#1c1b19]/40"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-white truncate" title={name}>
                                    {name}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400">
                                    {a.mime_type ? a.mime_type : ""}
                                    {a.size ? ` • ${(Number(a.size) / 1024).toFixed(0)} KB` : ""}
                                  </div>
                                  {a.description ? <div className="mt-2 text-xs text-gray-300">{a.description}</div> : null}

                                  {url ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-2 inline-block text-xs text-amber-400 hover:text-amber-300 underline"
                                    >
                                      Open file
                                    </a>
                                  ) : (
                                    <div className="mt-2 text-xs text-gray-500">No file path.</div>
                                  )}
                                </div>

                                <button
                                  onClick={() => handleRemoveAttachment(a)}
                                  disabled={isRemoving}
                                  className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                                    isRemoving
                                      ? "border-zinc-700 text-gray-500 cursor-not-allowed"
                                      : "border-red-700 text-red-300 hover:bg-red-700/20"
                                  }`}
                                  title={isRemoving ? "Removing..." : "Remove attachment"}
                                >
                                  {isRemoving ? "Removing..." : "Remove"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">No attachments.</div>
                    )}
                  </div>

                  {/* Upload new attachments */}
                  <div className="mt-5 border-t border-zinc-700 pt-4">
                    <div className="text-xs text-gray-400 mb-2">Add new attachments</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <input
                          type="file"
                          multiple
                          onChange={handlePickFiles}
                          className="w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-amber-600 file:text-black hover:file:bg-amber-700"
                        />
                        <div className="text-[11px] text-white/50 mt-1">Allowed: jpg, jpeg, png, pdf (max 10MB each)</div>

                        {newFiles.length > 0 && (
                          <div className="mt-2 text-xs text-white/70">
                            Selected: <span className="text-white">{newFiles.map((f) => f.name).join(", ")}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <input
                          type="text"
                          placeholder="Attachment description (optional)"
                          value={newFilesDesc}
                          onChange={(e) => setNewFilesDesc(e.target.value)}
                          className="w-full bg-transparent text-white py-2 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />

                        <div className="mt-3 flex items-center gap-2">
                          <SecondaryButton
                            onClick={handleUploadAttachments}
                            disabled={isUploadingAttachments || newFiles.length === 0}
                            className="bg-amber-600 text-black hover:bg-amber-700"
                          >
                            {isUploadingAttachments ? (
                              <>
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></span>
                                Uploading...
                              </>
                            ) : (
                              "Upload Attachments"
                            )}
                          </SecondaryButton>

                          <SecondaryButton
                            variant="outline"
                            onClick={() => {
                              setNewFiles([]);
                              setNewFilesDesc("");
                            }}
                            disabled={isUploadingAttachments}
                            className="text-white border-white hover:bg-white/10"
                          >
                            Clear
                          </SecondaryButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal actions */}
                <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-zinc-600">
                  <SecondaryButton
                    variant="outline"
                    onClick={() => {
                      setEditModalOpen(false);
                      setCurrentEditExpense(null);
                      setNewFiles([]);
                      setNewFilesDesc("");
                    }}
                    className="text-white border-white hover:bg-white/10"
                  >
                    Cancel
                  </SecondaryButton>

                  <SecondaryButton
                    onClick={handleSaveEdit}
                    className="bg-amber-600 text-black hover:bg-amber-700"
                    disabled={isSaving || loadingExpenseDetails}
                  >
                    {isSaving ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></span>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </SecondaryButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />}
    </AuthenticatedLayout>
  );
}

export default DriverExpense;
